/**
 * Freshness rule for GlobalStateProvider's per-client program-assignments cache.
 *
 * Why failures are cached too: ClientManagement re-runs its "load assignments for
 * every client" effect whenever the global-state context value changes, which is
 * on nearly every provider render. A successful read is absorbed by the 30s TTL,
 * but a failed read used to cache nothing. So while
 * GET /api/clients/program-assignments was failing, every render fired one
 * request per client: a request storm against an endpoint that was already
 * unhealthy. A failure is now remembered for a short backoff, and its stale
 * data (if any) is served meanwhile.
 */

export const ASSIGNMENTS_CACHE_TTL_MS = 30_000;
export const ASSIGNMENTS_FAILURE_BACKOFF_MS = 10_000;

export interface AssignmentsCacheEntry<T> {
  data: T;
  /** When this entry was written. */
  timestamp: number;
  /** True when the write recorded a failed fetch (data is the previous value or empty). */
  failed?: boolean;
}

/** Whether `entry` may be served without refetching at time `now`. */
export function isAssignmentsCacheFresh<T>(entry: AssignmentsCacheEntry<T> | undefined, now: number): boolean {
  if (!entry) return false;
  const window = entry.failed ? ASSIGNMENTS_FAILURE_BACKOFF_MS : ASSIGNMENTS_CACHE_TTL_MS;
  return now - entry.timestamp < window;
}

/** Entry to store after a failed fetch: keeps any previous data, starts the backoff. */
export function failedAssignmentsEntry<T>(
  previous: AssignmentsCacheEntry<T> | undefined,
  empty: T,
  now: number,
): AssignmentsCacheEntry<T> {
  return { data: previous?.data ?? empty, timestamp: now, failed: true };
}
